import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Textarea,
  Box,
  Text,
  AlertStatus,
  Grid,
  Badge,
  Radio,
  RadioGroup,
  Stack,
  Input,
  IconButton,
} from '@chakra-ui/react';
import ModalHeaderComponent from '../../../../components/ModalHeader';
import { useEffect, useRef, useState } from 'react';
import useAppToast from '../../../../hooks/useAppToast/useAppToast';
import useErrorPage from '../../../../hooks/useErrorPage/useErrorPage';
import { getDateAndTimeInterval } from '../../../../components/Format';
import { requestServerApi, serverApi } from '../../../../api/api';
import { getBadgePropsByStatus } from '../../../../utils/badgeUtils';
import { HttpStatus } from '@nestjs/common/enums';
import { AxiosResponse } from 'axios';
import useFile from '../../../../hooks/useFile/useFile';
import { MinusIcon } from '@chakra-ui/icons';

type Props = {
  onClose: () => void;
  setRefreshFlg: (flg: boolean) => void;
  feed_back: string;
  start_datetime: string;
  end_datetime: string;
  company_id: string;
  lesson_id: string;
  learner_id: string;
  learner_name?: string;
  participation_status?: string;
  admin_comment?: string;
  t_lesson_learner_documents?: any[];
  payment_id?: number;
};

// Type for each file entry
type FileEntry = {
  id: number;
  shareType: 'file' | 'url';
  file: File | null;
  url: string;
};

export default function FeedbackEditModal({
  onClose,
  setRefreshFlg,
  feed_back,
  admin_comment,
  start_datetime,
  end_datetime,
  company_id,
  lesson_id,
  learner_id,
  learner_name,
  participation_status,
  t_lesson_learner_documents,
  payment_id,
}: Props) {
  const [confirming, setConfirming] = useState(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [newFeedback, setNewFeedback] = useState<string>(feed_back || '');
  const { apiErrorHandler } = useErrorPage();
  const { handleDownload } = useFile();
  const { displayToast } = useAppToast();
  const [newAdminComment, setNewAdminComment] = useState(admin_comment || '');

  const [totalLessons, setTotalLessons] = useState(0);
  const [participatedLessons, setParticipatedLessons] = useState(0);
  const [fileEntries, setFileEntries] = useState<FileEntry[]>([
    { id: Date.now(), shareType: 'file', file: null, url: '' },
  ]);

  const addFileEntry = () => {
    setFileEntries((prev) => [
      ...prev,
      { id: Date.now(), shareType: 'file', file: null, url: '' },
    ]);
  };

  const removeFileEntry = (id: number) => {
    setFileEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const updateFileEntry = (id: number, changes: Partial<FileEntry>) => {
    setFileEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry))
    );
  };

  useEffect(() => {
    // ここでAPIを呼び出してtotalLessonsとparticipatedLessonsを取得する
    const getStudentsLessonParticipationInfo = async (learnerId: string, companyId: number) => {
      try {
        const result = await requestServerApi<Promise<void | AxiosResponse<any>>>(() =>
          serverApi.learnerControllerGetLearnerTotalLessonsVsParticipatedLessons(
            learnerId,
            companyId,
            payment_id
          ).catch((e) =>
            apiErrorHandler(e, {
              componentName: 'feedback',
              functionName: serverApi.learnerControllerGetLearnerTotalLessonsVsParticipatedLessons.name,
            })
          )
        );
        const responseData = result?.data;
        if (responseData && responseData.code === HttpStatus.OK) {
          setTotalLessons(responseData?.data?.totalLessons || 0);
          setParticipatedLessons(responseData?.data?.participatedLessons || 0);
        } else {
          console.error('APIエラー', responseData?.message || '不明なエラー');
        }
      } catch (error) {
        console.error('エラー', error);
      }
    };

    getStudentsLessonParticipationInfo(learner_id, parseInt(company_id));
  }, [learner_id, company_id, apiErrorHandler, payment_id]);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const closeModal = () => {
    onClose();
  };

  useEffect(() => {
    if (!confirming) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [confirming]);

  const handleInputChange = (e: any) => {
    setNewFeedback(e.target.value);
  };

  // フィードバック情報更新
  const onFeedbackUpdate = () => {
    setIsUpdating(true);
    const validFileEntries = fileEntries.filter((entry) =>
      (entry.shareType === 'file' && entry.file !== null) ||
      (entry.shareType === 'url' && entry.url.trim() !== '')
    );

    const filesMetadata = validFileEntries.map((entry) => ({
      is_internal_file: entry.shareType === 'file',
      file_url: entry.shareType === 'url' ? entry.url : '',
    }));

    const internalFiles = validFileEntries
      .filter((entry) => entry.shareType === 'file' && entry.file)
      .map((entry) => entry.file as File);

    // Build a FormData with actual files to merge into the generated one
    const extraFormData = new FormData();
    internalFiles.forEach((file) => {
      extraFormData.append('files', file); // "files" must match FilesInterceptor("files") in controller
    });

    const apiResponse = requestServerApi<any>(() =>
      serverApi.organizationsControllerUpdateLessonFeedback(
        company_id,
        lesson_id,
        newFeedback,
        learner_id,
        newAdminComment,
        JSON.stringify(filesMetadata),
        {
          transformRequest: [
            (data: FormData) => {
              // append files into it before sending
              internalFiles.forEach((file) => {
                data.append('files', file);
              });
              return data;
            },
          ],
        }
      )
    );

    apiResponse.then((result) => {
      reactToCreation(result.data.message, 'フィードバック更新');
      closeModal();
      setRefreshFlg(true);
    });
  };

  //　登録後の処理
  const reactToCreation = (apiResponseMsg: string, toastTitle: string) => {
    var toastDescription: string =
      apiResponseMsg === 'NO_ERROR' ? `${toastTitle}をしました。` : `${toastTitle}に失敗しました。`;
    var toastStatus: AlertStatus = apiResponseMsg === 'NO_ERROR' ? 'success' : 'error';
    displayToast(toastTitle, toastDescription, toastStatus);
  };
  return (
    <Modal isOpen={true} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent minW="55rem">
        <ModalHeaderComponent
          title={confirming === false ? 'フィードバック編集' : 'フィードバック編集　確認'}
          disableClose={isUpdating}
        />
        <ModalBody>
          <Grid templateColumns="auto auto" columnGap="40px">
            {/* 実施日時 */}
            <Flex direction="column">
              <Text fontWeight="bold">実施日時</Text>
              <Text>{getDateAndTimeInterval(start_datetime, end_datetime)}</Text>
            </Flex>

            {/* 回数 */}
            <Flex direction="column">
              <Text fontWeight="bold">回数</Text>
              <Text>{participatedLessons} / {totalLessons}</Text>
            </Flex>
          </Grid>
          <Flex direction="column" mt={5}>
            <Text fontWeight="bold">受講者</Text>

            {/* 受講者名 */}
            <Flex mt={2} align="center" justify="space-between" maxW="300px">
              <Text>{learner_name}</Text>
              {(() => {
                const badge = getBadgePropsByStatus(participation_status ?? '');
                return (
                  <Badge colorScheme={badge.colorScheme} fontSize="md" px={2} py={1}>
                    {badge.label}
                  </Badge>
                );
              })()}
            </Flex>
          </Flex>
          <Box mt={5} overflowY="auto">
            <Text fontWeight={'bold'}>内容</Text>
          </Box>
          {confirming ? (
            <Text mt={2} whiteSpace="pre-wrap">{newFeedback || 'フィードバックが入力されていません'}</Text>
          ) : (
            <Textarea
              ref={textareaRef}
              mt={2}
              rows={10}
              placeholder="レッスンの内容や学習者についてのフィードバックを記入してください。"
              onChange={handleInputChange}
              value={newFeedback}
            />
          )}
          {/* ★ 学習者の管理者へのコメント */}
          <Box mt={5}>
            <Text fontWeight="bold">
              学習者の管理者へのコメント（学習者には見えません）
            </Text>

            {confirming ? (
              <Text mt={2} whiteSpace="pre-wrap">
                {newAdminComment || 'コメントが入力されていません'}
              </Text>
            ) : (
              <Textarea
                mt={2}
                rows={5}
                onChange={(e) => setNewAdminComment(e.target.value)}
                placeholder="学習者の管理者へのコメントを記入してください。"
                value={newAdminComment}
              />
            )}
          </Box>
          <Box mt={5}>
            <Flex justify="space-between" align="center">
              <Text fontWeight="bold">共有資料</Text>
              {/* Add new file entry button — hidden in confirming mode */}
              {!confirming && (
                <Button size="sm" colorScheme="teal" variant="outline" onClick={addFileEntry}>
                  ＋ 追加
                </Button>
              )}
            </Flex>

            {t_lesson_learner_documents && t_lesson_learner_documents.length > 0 ? (
              t_lesson_learner_documents
                .filter((doc: any) => !doc.delete_flg)
                .map((doc: any, index: number) => (
                  <Flex
                    key={index}
                    mt={3}
                    p={3}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    align="center"
                    justify="space-between"
                  >
                    {/* File name or URL */}
                    <Flex direction="column" maxW="70%">
                      <Text fontSize="sm" color="gray.500">
                        {doc.is_internal_file ? 'ファイル' : 'URL'}
                      </Text>
                      <Text
                        fontSize="sm"
                        color="black"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {doc.is_internal_file
                          ? doc.file_url.split('/').pop() // show filename from S3 URL
                          : doc.file_url}
                      </Text>
                    </Flex>

                    {/* Download / Open button */}
                    <Button
                      size="sm"
                      colorScheme="teal"
                      variant="outline"
                      onClick={() =>
                        doc.is_internal_file
                          ? handleDownload(doc.file_url) // handle file download
                          : window.open(doc.file_url, '_blank') // open URL in new tab
                      }
                    >
                      {doc.is_internal_file ? 'ダウンロード' : '開く'}
                    </Button>
                  </Flex>
                ))
            ) : (
              <Text mt={2} color="gray.500">
                共有資料はありません
              </Text>
            )}

            {fileEntries.map((entry, index) => (
              <Box key={entry.id} mt={4} p={3} border="1px solid" borderColor="gray.200" borderRadius="md">
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.500">資料 {index + 1}</Text>

                  {/* Remove button — hidden in confirming mode */}
                  {!confirming && fileEntries.length > 1 && (
                    <IconButton
                      aria-label="削除"
                      icon={<MinusIcon />}
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => removeFileEntry(entry.id)}
                    />
                  )}
                </Flex>

                {confirming ? (
                  // Confirm view — show summary of each entry
                  <Text mt={2} fontSize="sm" color="black">
                    {entry.shareType === 'file'
                      ? entry.file?.name || 'ファイル未選択'
                      : entry.url || 'URLが入力されていません'}
                  </Text>
                ) : (
                  // Edit view
                  <>
                    <RadioGroup
                      mt={2}
                      value={entry.shareType}
                      onChange={(value: 'file' | 'url') =>
                        updateFileEntry(entry.id, { shareType: value, file: null, url: '' })
                      }
                    >
                      <Stack direction="row">
                        <Radio value="file">ファイル</Radio>
                        <Radio value="url">URL</Radio>
                      </Stack>
                    </RadioGroup>

                    {entry.shareType === 'file' ? (
                      <Input
                        mt={2}
                        type="file"
                        onChange={(e) =>
                          updateFileEntry(entry.id, { file: e.target.files?.[0] || null, url: '' })
                        }
                      />
                    ) : (
                      <Input
                        mt={2}
                        type="text"
                        placeholder="https://example.com"
                        value={entry.url}
                        onChange={(e) =>
                          updateFileEntry(entry.id, { url: e.target.value, file: null })
                        }
                      />
                    )}
                  </>
                )}
              </Box>
            ))}
          </Box>
        </ModalBody>
        <ModalFooter>
          {confirming === false ? (
            <Flex justifyContent="right">
              <Button
                colorScheme="teal"
                mr={3}
                onClick={() => setConfirming(true)}
                disabled={
                  isUpdating ||
                  ((newFeedback === feed_back && newAdminComment === admin_comment) &&
                    // check if first element of fileEntries is empty (default state) to prevent confirming without any input, or if all entries are empty
                    ((fileEntries.length === 1 &&
                      fileEntries[0].shareType === 'file' &&
                      !fileEntries[0].file) ||
                      (fileEntries.length === 1 &&
                        fileEntries[0].shareType === 'url' &&
                        fileEntries[0].url.trim() === ''
                      ))
                  )
                }
              >
                確認
              </Button>
              <Button colorScheme="gray" onClick={closeModal}>
                キャンセル
              </Button>
            </Flex>
          ) : (
            <Flex justifyContent="right">
              <Button
                colorScheme="teal"
                mr={3}
                onClick={() => onFeedbackUpdate()}
                disabled={isUpdating}
              >
                確定
              </Button>
              <Button colorScheme="gray" onClick={() => setConfirming(false)} disabled={isUpdating}>
                修正
              </Button>
            </Flex>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
